import styled from '@emotion/styled'
import {
  ExtranetTicketListItemDto,
  ExtranetTicketStatusCodeEnum,
} from '@pharmac-extranet/extranet-api'
import {
  AlertIcon,
  CheckIcon,
  ClockIcon,
  ClosedIcon,
  ExtranetCatalogItemChevron,
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTableColumn,
  ExtranetTableGroupColumn,
  ExtranetTableRow,
  PersonIcon,
  StarIcon,
  formatDateTimeString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacTruncate } from '@pharmac/ui'
import { TicketsListItemDetails } from './TicketsListItemDetails'

type OpenTicketsListItemProps = {
  ticket: ExtranetTicketListItemDto
}

const SubjectWrapper = styled.div`
  max-height: calc(50px - (2 * var(--spacing-small)));
  text-overflow: ellipsis;
`

export const TicketsListItem = ({ ticket }: OpenTicketsListItemProps) => {
  const { minDesktop } = useBreakpoint()
  const { t } = useLanguage()

  const ticketStatus = (status: string) => {
    switch (status) {
      case ExtranetTicketStatusCodeEnum.IncidentStatusNew:
        return (
          <>
            <StarIcon icon="star" /> {t('extranet:contact.new')}
          </>
        )
      case ExtranetTicketStatusCodeEnum.IncidentStatusResolved:
        return (
          <>
            <CheckIcon icon="checkCircle" /> {t('extranet:contact.resolved')}
          </>
        )
      case ExtranetTicketStatusCodeEnum.IncidentStatusClosed:
        return (
          <>
            <ClosedIcon icon="checkCircle" /> {t('extranet:contact.closed')}
          </>
        )
      case ExtranetTicketStatusCodeEnum.IncidentStatusCancelledbycustomer:
        return (
          <>
            <AlertIcon icon="xCircle" /> {t('extranet:contact.cancelled')}
          </>
        )
      case ExtranetTicketStatusCodeEnum.IncidentStatusWaitingforcustomer:
        return (
          <>
            <PersonIcon icon="user" /> {t('extranet:contact.repliedByPharmac')}
          </>
        )
      case ExtranetTicketStatusCodeEnum.IncidentStatusSolving:
      case ExtranetTicketStatusCodeEnum.IncidentStatusWaitingfor3Rdparty:
      case ExtranetTicketStatusCodeEnum.IncidentStatusReassigned:
      default:
        return (
          <>
            <ClockIcon icon="clock" /> {t('extranet:contact.inProgress')}
          </>
        )
    }
  }

  const parseIncidentId = (name: string) => {
    return name.split('-').slice(1, 2)
  }

  return (
    <ExtranetTableRow
      style={{
        backgroundColor: 'var(--color-accent-1)',
      }}
      expand={<TicketsListItemDetails ticketId={ticket.id} />}
      toggleExpand={() => null}
      children={({ isExpanded }) => (
        <ExtranetTableGroupColumn>
          <ExtranetTableRow>
            {minDesktop ? (
              <>
                <ExtranetTableColumn
                  size={0.6}
                  flexJustify="center"
                  style={{
                    backgroundColor: ticket.isPrivate
                      ? 'var(--color-primary)'
                      : 'var(--color-accent-3)',
                    color: 'var(--color-white)',
                  }}
                >
                  {ticket.isPrivate && (
                    <PharmacIcon
                      size="18"
                      icon="lock"
                    />
                  )}
                </ExtranetTableColumn>
                <ExtranetTableColumn
                  marginSmallLeft
                  size={1.5}
                >
                  {parseIncidentId(ticket.incidentId)}
                </ExtranetTableColumn>
                <ExtranetTableColumn size={4}>
                  <SubjectWrapper>{PharmacTruncate(ticket.subject, 30)}</SubjectWrapper>
                </ExtranetTableColumn>
                <ExtranetTableColumn size={2.5}>
                  <ExtranetContainer
                    full
                    flexRow
                    alignCenter
                  >
                    {ticketStatus(ticket.statusCode)}
                  </ExtranetContainer>
                </ExtranetTableColumn>
                <ExtranetTableColumn size={2.5}>
                  {formatDateTimeString(ticket.created)}
                </ExtranetTableColumn>
                <ExtranetTableColumn size={2.5}>
                  {formatDateTimeString(ticket.updated)}
                </ExtranetTableColumn>
                <ExtranetTableColumn size={4}>{ticket.extranetUserName}</ExtranetTableColumn>
              </>
            ) : (
              <>
                <ExtranetTableColumn
                  size={0.6}
                  flexJustify="center"
                  style={{
                    backgroundColor: ticket.isPrivate
                      ? 'var(--color-primary)'
                      : 'var(--color-accent-2)',
                    color: 'var(--color-white)',
                  }}
                >
                  {ticket.isPrivate && (
                    <PharmacIcon
                      size="18"
                      icon="lock"
                    />
                  )}
                </ExtranetTableColumn>
                <ExtranetTableColumn
                  marginSmallLeft
                  size={2}
                >
                  <ExtranetContainer
                    full
                    flexRow
                    alignCenter
                  >
                    {ticketStatus(ticket.statusCode)}
                  </ExtranetContainer>
                </ExtranetTableColumn>
                <ExtranetTableColumn size={2}>
                  <SubjectWrapper>{PharmacTruncate(ticket.subject, 25)}</SubjectWrapper>
                </ExtranetTableColumn>
              </>
            )}
            <ExtranetExpandArrowColumn>
              <ExtranetCatalogItemChevron isExpanded={isExpanded} />
            </ExtranetExpandArrowColumn>
          </ExtranetTableRow>
        </ExtranetTableGroupColumn>
      )}
    />
  )
}
